import type { CursorPosition, LineRef, StrokePreview } from './types';

/**
 * Converts a cell index to row and column coordinates
 * @param cellIndex - The cell index (0-based)
 * @param columns - Number of columns in the board
 * @returns Object with row and col properties
 */
export function cellIndexToRowCol(cellIndex: number, columns: number): { row: number; col: number } {
  return {
    row: Math.floor(cellIndex / columns),
    col: cellIndex % columns
  };
}

/** True when the cell lies in the row or column `line`. */
export function lineContains(line: LineRef, cellIndex: number, columns: number): boolean {
  const { row, col } = cellIndexToRowCol(cellIndex, columns);
  return line.axis === 'row' ? row === line.index : col === line.index;
}

/** The row and the column through a cell. */
export function cellLines(cellIndex: number, columns: number): [LineRef, LineRef] {
  const { row, col } = cellIndexToRowCol(cellIndex, columns);
  return [{ axis: 'row', index: row }, { axis: 'column', index: col }];
}

/** A string naming a line, for map and set keys ("row:3", "column:0"). */
export function lineKey(line: LineRef): string {
  return `${line.axis}:${line.index}`;
}

/** Indices of a row's or column's cells, first to last. */
export function lineCellIndices(line: LineRef, rows: number, columns: number): number[] {
  return line.axis === 'row'
    ? Array.from({ length: columns }, (_, col) => rowColToCellIndex(line.index, col, columns))
    : Array.from({ length: rows }, (_, row) => rowColToCellIndex(row, line.index, columns));
}

/**
  * Converts row and column coordinates to a cell index
  * @param row - Row index (0-based)
  * @param col - Column index (0-based)
  * @param columns - Number of columns in the board
  * @returns Cell index (0-based)
  */
export function rowColToCellIndex(row: number, col: number, columns: number): number {
  return row * columns + col;
}

/**
 * Validates if a cell position is within board bounds
 * @param row - Row index to validate
 * @param col - Column index to validate
 * @param rows - Total number of rows in the board
 * @param columns - Total number of columns in the board
 * @returns True if the position is valid, false otherwise
 */
export function isValidCellPosition(row: number, col: number, rows: number, columns: number): boolean {
  return row >= 0 && row < rows && col >= 0 && col < columns;
}

/**
* Validates if a cell index is within board bounds
* @param cellIndex - Cell index to validate
* @param totalCells - Total number of cells (rows * columns)
* @returns True if the index is valid, false otherwise
*/
export function isValidCellIndex(cellIndex: number, totalCells: number): boolean {
  return cellIndex >= 0 && cellIndex < totalCells;
}

/**
 * Calculates the horizontal run (consecutive cells with the same value) at a specific position in a row
 * @param board - The board array
 * @param row - Row index where the cursor is
 * @param col - Column index
 * @param columns - Total number of columns
 * @returns Object with { value: number, length: number, startCol: number, endCol: number }
 */
export function getHorizontalRun(board: readonly number[], row: number, col: number, columns: number): { value: number; length: number; startCol: number; endCol: number } {
  const cellIndex = rowColToCellIndex(row, col, columns);
  const cellValue = board[cellIndex];

  // Find start of run (go left)
  let startCol = col;
  while (startCol > 0) {
    const prevIndex = rowColToCellIndex(row, startCol - 1, columns);
    if (board[prevIndex] === cellValue) {
      startCol--;
    } else {
      break;
    }
  }

  // Find end of run (go right)
  let endCol = col;
  while (endCol < columns - 1) {
    const nextIndex = rowColToCellIndex(row, endCol + 1, columns);
    if (board[nextIndex] === cellValue) {
      endCol++;
    } else {
      break;
    }
  }

  return {
    value: cellValue,
    length: endCol - startCol + 1,
    startCol,
    endCol
  };
}

/**
 * Calculates the vertical run (consecutive cells with the same value) at a specific position in a column
 * @param board - The board array
 * @param row - Row index where the cursor is
 * @param col - Column index
 * @param rows - Total number of rows
 * @param columns - Total number of columns
 * @returns Object with { value: number, length: number, startRow: number, endRow: number }
 */
/**
 * Gets all cell indices within a rectangular region
 * @param startCellIndex - Starting cell index (top-left or any corner)
 * @param endCellIndex - Ending cell index (bottom-right or any corner)
 * @param columns - Number of columns in the board
 * @returns Array of all cell indices within the rectangle (inclusive)
 */
export function getRectangleCells(startCellIndex: number, endCellIndex: number, columns: number): number[] {
  const start = cellIndexToRowCol(startCellIndex, columns);
  const end = cellIndexToRowCol(endCellIndex, columns);

  const minRow = Math.min(start.row, end.row);
  const maxRow = Math.max(start.row, end.row);
  const minCol = Math.min(start.col, end.col);
  const maxCol = Math.max(start.col, end.col);

  const cells: number[] = [];

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      cells.push(rowColToCellIndex(row, col, columns));
    }
  }

  return cells;
}

/**
 * Calculates the vertical run (consecutive cells with the same value) at a specific position in a column
 * @param board - The board array
 * @param row - Row index where the cursor is
 * @param col - Column index
 * @param rows - Total number of rows
 * @param columns - Total number of columns
 * @returns Object with { value: number, length: number, startRow: number, endRow: number }
 */
export function getVerticalRun(board: readonly number[], row: number, col: number, rows: number, columns: number): { value: number; length: number; startRow: number; endRow: number } {
  const cellIndex = rowColToCellIndex(row, col, columns);
  const cellValue = board[cellIndex];

  // Find start of run (go up)
  let startRow = row;
  while (startRow > 0) {
    const prevIndex = rowColToCellIndex(startRow - 1, col, columns);
    if (board[prevIndex] === cellValue) {
      startRow--;
    } else {
      break;
    }
  }

  // Find end of run (go down)
  let endRow = row;
  while (endRow < rows - 1) {
    const nextIndex = rowColToCellIndex(endRow + 1, col, columns);
    if (board[nextIndex] === cellValue) {
      endRow++;
    } else {
      break;
    }
  }

  return {
    value: cellValue,
    length: endRow - startRow + 1,
    startRow,
    endRow
  };
}

/**
 * Compute the flat cell indices affected by a stroke from origin to current.
 * Shared by preview (on pointer-move) and commit (on pointer-up) so they can never drift.
 *
 * @param origin  Cell where the stroke started.
 * @param current Cell the pointer is currently over.
 * @param mode    Draw mode at stroke start.
 * @param columns Total columns in the board.
 * @param lockedAxis Optional axis lock for line mode.
 * @returns Sorted array of flat cell indices.
 */
export function computeStrokeCells(
  origin: { row: number; col: number },
  current: { row: number; col: number },
  mode: 'paint' | 'line' | 'rectangle',
  columns: number,
  lockedAxis?: 'row' | 'column' | null,
): number[] {
  switch (mode) {
    case 'paint': {
      // Paint mode: just the single current cell.
      return [rowColToCellIndex(current.row, current.col, columns)];
    }
    case 'line': {
      // Line mode: straight line locked to one axis.
      const axis = lockedAxis ?? detectLineAxis(origin, current);
      if (axis === 'row') {
        // Horizontal line on origin.row from origin.col to current.col.
        const minCol = Math.min(origin.col, current.col);
        const maxCol = Math.max(origin.col, current.col);
        const indices: number[] = [];
        for (let c = minCol; c <= maxCol; c++) {
          indices.push(rowColToCellIndex(origin.row, c, columns));
        }
        return indices;
      } else {
        // Vertical line on origin.col from origin.row to current.row.
        const minRow = Math.min(origin.row, current.row);
        const maxRow = Math.max(origin.row, current.row);
        const indices: number[] = [];
        for (let r = minRow; r <= maxRow; r++) {
          indices.push(rowColToCellIndex(r, origin.col, columns));
        }
        return indices;
      }
    }
    case 'rectangle': {
      return getRectangleCells(
        rowColToCellIndex(origin.row, origin.col, columns),
        rowColToCellIndex(current.row, current.col, columns),
        columns,
      );
    }
  }
}

/** Dominant orthogonal axis from origin to current. Tie (and no movement) is horizontal. */
export function detectLineAxis(
  origin: { row: number; col: number },
  current: { row: number; col: number },
): 'row' | 'column' {
  const dRow = Math.abs(current.row - origin.row);
  const dCol = Math.abs(current.col - origin.col);
  return dCol >= dRow ? 'row' : 'column';
}

/**
 * Pin a cursor onto the orthogonal line from origin.
 *
 * Axis is taken from `lockedAxis`, or detected from the nearest cells to
 * `origin`/`current` so fractional (smooth) positions do not chatter.
 * The locked coordinate is copied from origin; the other follows `current`,
 * so a fractional column still slides along a row-locked line.
 */
export function lockLineCursor(
  origin: { row: number; col: number },
  current: { row: number; col: number },
  lockedAxis?: 'row' | 'column' | null,
): { row: number; col: number; lockedAxis: 'row' | 'column' } {
  const axis = lockedAxis ?? detectLineAxis(
    { row: Math.round(origin.row), col: Math.round(origin.col) },
    { row: Math.round(current.row), col: Math.round(current.col) },
  );
  return axis === 'row'
    ? { row: origin.row, col: current.col, lockedAxis: 'row' }
    : { row: current.row, col: origin.col, lockedAxis: 'column' };
}

/**
 * Overlay an in-progress stroke onto a copy of the board.
 * Returns the original array when there is nothing to apply (including note
 * and note arrow strokes, which never change cells).
 */
export function applyStrokePreview(board: number[], preview: StrokePreview | null): number[] {
  if (!preview || preview.note || preview.arrow || preview.indices.length === 0) return board;
  const next = board.slice();
  for (const idx of preview.indices) {
    if (isValidCellIndex(idx, next.length)) next[idx] = preview.value;
  }
  return next;
}

export interface LineDragRunIndicator {
  length: number;
  value: number;
  row: number;
  column: number;
}

/**
 * Connected run on the locked line-drag axis at the locked cursor cell.
 * `board` should already include the in-progress preview.
 */
export function getLineDragRunIndicator(
  board: number[],
  rows: number,
  columns: number,
  origin: CursorPosition,
  current: CursorPosition,
  lockedAxis?: 'row' | 'column' | null,
): LineDragRunIndicator | null {
  const locked = lockLineCursor(
    { row: origin.row, col: origin.column },
    { row: current.row, col: current.column },
    lockedAxis,
  );
  const row = Math.round(locked.row);
  const column = Math.round(locked.col);
  if (!isValidCellPosition(row, column, rows, columns)) return null;

  const run = locked.lockedAxis === 'row'
    ? getHorizontalRun(board, row, column, columns)
    : getVerticalRun(board, row, column, rows, columns);

  return { length: run.length, value: run.value, row, column };
}

/** Cells on the straight line between two cells, ends included (Bresenham): the cells a fast drag skipped. */
export function cellsBetween(from: number, to: number, columns: number): number[] {
  let x0 = from % columns;
  let y0 = Math.floor(from / columns);
  const x1 = to % columns;
  const y1 = Math.floor(to / columns);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let error = dx + dy;
  const cells: number[] = [];
  for (;;) {
    cells.push(y0 * columns + x0);
    if (x0 === x1 && y0 === y1) return cells;
    const doubled = 2 * error;
    if (doubled >= dy) {
      error += dy;
      x0 += sx;
    }
    if (doubled <= dx) {
      error += dx;
      y0 += sy;
    }
  }
}
